import { useMemo } from 'react';
import { useGetAccountQuery, useGetSmartLinksQuery } from '../../redux/api/fymAPI';
import { useMusic24Capabilities } from '../music24/hooks/useMusic24Capabilities';

export interface PublishedPagesUsage {
  /** `null` on an unlimited plan — nothing to meter or lock. */
  limit: number | null;
  /** `undefined` while the count hasn't loaded yet (only meaningful once `limit` isn't `null`). */
  used: number | undefined;
}

/**
 * How many of the account's SmartLink pages are published, against the plan's page limit.
 * Excludes the account's own Bio Link (`mainSmartLinkId`) — the plan counts release pages, not
 * the profile page every account already has.
 *
 * Shared by every Publish entry point (the manage screen's publish bar, the editor toolbar), so
 * "used" is computed once and every lock agrees with the others. A hint only: the server is the
 * authority, and a publish over the cap still comes back as `M24_LIMIT_PAGES`.
 */
export function usePublishedPagesUsage(accountId: number, options: { skip?: boolean } = {}): PublishedPagesUsage {
  const { publishedPagesLimit } = useMusic24Capabilities();
  // `skip` holds the reads back while the caller has no account yet (no `GET /accounts/0`).
  const tracksPagesLimit = publishedPagesLimit !== null && options.skip !== true;
  const { data: accountData } = useGetAccountQuery({ accountId }, { skip: !tracksPagesLimit });
  // `page` is 0-indexed (the list view's own call sends `page=0` for the first page) and the
  // endpoint refuses a `limit` over 100 — two bugs this hook silently carried since Task 3, both
  // of which left `used` undefined and the lock never showing (a wrong `page` reads past the end
  // of a short list, an over-limit `limit` 400s). 100 covers every plan that reaches this hook:
  // only Free has a numeric `publishedPagesLimit` today, capped at 3 published pages.
  const { data: smartLinksList } = useGetSmartLinksQuery(
    { accountId, page: 0, limit: 100 },
    { skip: !tracksPagesLimit }
  );

  const used = useMemo(() => {
    if (!tracksPagesLimit || accountData === undefined || smartLinksList === undefined) {
      return undefined;
    }
    const { mainSmartLinkId } = accountData.account;
    return smartLinksList.results.filter(
      (link) => link.publishedSmartLinkVersion !== null && link.id !== mainSmartLinkId
    ).length;
  }, [tracksPagesLimit, accountData, smartLinksList]);

  return { limit: publishedPagesLimit, used };
}
